import { Location } from '@cobliteam/common-types';
import { type Permission, permissions } from '@cobliteam/permissions';

/**
 * Data-only model for the gamified "Central de Configuração da Frota" (fleet maturity center).
 *
 * No JSX / no icons / no i18n here on purpose: this file is shared by `useBasicValueMaturity`
 * (in the `@cobliteam/hooks` package) and is consumed both by the BasicValue page (apps/dashboard)
 * and by the topbar avatar ring (libs/ui-business). Icons and copy live at the presentation layer,
 * keyed by item id.
 */

/** Minimum number of records a fleet must have for a maturity item to count as done. */
export const MATURITY_MIN_COUNT = 2;

/**
 * Fraction of the fleet's vehicles that must be associated (with a driver, or with a device) for the
 * "driver-association" / "device-association" items to count as done. Requiring 100% — or a fixed count
 * of 2 — is too strict for real/small fleets (a single stray vehicle or a fleet with one device blocks
 * it), so a majority threshold is used: at least half the vehicles need the association.
 */
export const ASSOCIATION_MIN_RATIO = 0.5;

/** The three setup areas the items are grouped under. */
export type MaturityAreaKey = 'installation' | 'setup' | 'configuration';

export type MaturityItemId =
  | 'devices'
  | 'vehicles'
  | 'device-association'
  | 'drivers'
  | 'driver-association'
  | 'users'
  | 'groups'
  | 'alerts'
  | 'speed'
  | 'geofences'
  | 'checklists';

/**
 * Meta por etapa, transcrita das tags do Figma (AE-2497): "0/5 veículos cadastrados", "0/2 usuários
 * convidados", "0/3 locais criados"… Etapas ausentes daqui não têm meta desenhada e mantêm a regra
 * anterior — `devices` e `speed` não exibem tag no Figma, e as duas associações são medidas por
 * proporção da frota (ver ASSOCIATION_MIN_RATIO), não por contagem fixa.
 */
export const MATURITY_ITEM_TARGET: Partial<Record<MaturityItemId, number>> = {
  users: 2,
  drivers: 5,
  vehicles: 5,
  groups: 1,
  alerts: 2,
  geofences: 3,
  checklists: 1,
};
export const MATURITY_AREAS: MaturityAreaKey[] = ['installation', 'setup', 'configuration'];

export interface MaturityItemModel {
  id: MaturityItemId;
  area: MaturityAreaKey;
  /** Existing product flow the "Configurar" CTA navigates to. */
  route: Location;
  /** Permission required to act on this item; items the user can't access are excluded from the %. */
  permission: Permission;
}

/**
 * The 11 maturity items, na ordem e no agrupamento do Figma (AE-2497). "Convide usuários" fica em
 * Instalação e o cadastro de veículos / associação com dispositivos em Cadastros — diferente do
 * agrupamento anterior.
 *
 * Note: there is no dedicated permission for device/tracker management, so "Instalação" reuses
 * `create-update-delete:crm` (the installation journey's permission); device↔vehicle association
 * and per-vehicle speed limits reuse the vehicle permission.
 */
export const MATURITY_ITEMS: MaturityItemModel[] = [
  // Instalação
  { id: 'devices', area: 'installation', route: Location.DEVICES, permission: permissions.CREATE_UPDATE_DELETE_CRM },
  { id: 'users', area: 'installation', route: Location.CONFIG_USERS, permission: permissions.CREATE_UPDATE_DELETE_USERS_AND_INVITES },

  // Cadastros
  { id: 'drivers', area: 'setup', route: Location.DRIVER_MANAGEMENT, permission: permissions.CREATE_UPDATE_DELETE_DRIVER },
  // Sem contrapartida no Figma (AE-2491): produto decidiu manter a etapa, e ela fica junto do cadastro
  // de motoristas, que é o assunto dela.
  { id: 'driver-association', area: 'setup', route: Location.DRIVER_MANAGEMENT, permission: permissions.CREATE_UPDATE_DELETE_DRIVER },
  { id: 'vehicles', area: 'setup', route: Location.VEHICLES, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },
  { id: 'device-association', area: 'setup', route: Location.DEVICES, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },
  { id: 'groups', area: 'setup', route: Location.VEHICLES_GROUPS, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },

  // Configurações
  { id: 'speed', area: 'configuration', route: Location.ROAD_SPEED, permission: permissions.CREATE_UPDATE_DELETE_VEHICLE },
  // Página de alarmes, não `ALERTS_TRIGGERS_CREATE`: aquela rota abre a modal de criação antiga, que o
  // fluxo do guia não deve alcançar (AE-2520). É também o destino certo do atalho "Meus alertas".
  { id: 'alerts', area: 'configuration', route: Location.ALERTS_TRIGGERS, permission: permissions.CONFIG_AND_VIEW_CUSTOM_ALERTS },
  { id: 'geofences', area: 'configuration', route: Location.GEOFENCES_CREATE, permission: permissions.CREATE_UPDATE_DELETE_PLACES_OF_INTEREST },
  { id: 'checklists', area: 'configuration', route: Location.CHECKLIST_ALL, permission: permissions.FULL_CONTROL_CHECKLIST },
];

/**
 * Status de uma linha de etapa:
 * - `done` — concluída
 * - `next` — a protagonista (primeira pendente do guia)
 * - `todo` — pendente e acionável (primeira pendente da sua área)
 * - `locked` — pendente e bloqueada: a etapa anterior da mesma área ainda não fechou (AE-2498)
 */
export type MaturityItemStatus = 'done' | 'next' | 'todo' | 'locked';

/** Status of an area node in the setup track. */
export type MaturityNodeStatus = 'done' | 'current' | 'locked';
